/**
 * @file NewPropertiesAssetUpload.js
 * @description Properties panel for asset upload container header configuration.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FileDrop } from '../helpers/FileDrop';
import './PropertiesMultimedia.css';
import Toast from '../ui/toasts/Toast';
import { uploadFile } from '../../services/containerService';
import { useUploadContext } from '../../store/Contexts';
import {
    formatFileSize,
    getAudioLength,
    getFileCategory,
    getImageDimensions,
    getVideoRuntime,
    sanitizeFileName,
} from '../helpers/FileUtils';
import {
    createAsset,
    fetchAssets,
    fetchAssetByID,
} from '../../services/clientService';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import Video from '../video-component/Video';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import AddFromAssetsModal from '../ui/modals/AddFromAssetsModal';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { Popover, Box } from '@mui/material';
import Spinner from '../ui/spinners/Spinner';
import { ReactComponent as ImageIcon } from '../ui/svg/Add Image Placeholder.svg';
import { ReactComponent as VideoIcon } from '../ui/svg/Add Video Placeholder.svg';
import { getText } from '../../i18n';

function isNotEmptyObject(obj) {
    return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
}

function buildImageUrls(imageAsset1, imageAsset2, imageAsset3, imageAsset4) {
    const imagesUrls = [];
    if (isNotEmptyObject(imageAsset1)) {
        imagesUrls.push(imageAsset1.url);
    }
    if (isNotEmptyObject(imageAsset2)) {
        imagesUrls.push(imageAsset2.url);
    }
    if (isNotEmptyObject(imageAsset3)) {
        imagesUrls.push(imageAsset3.url);
    }
    if (isNotEmptyObject(imageAsset4)) {
        imagesUrls.push(imageAsset4.url);
    }
    return imagesUrls;
}

function buildContainerAssets(
    imageAsset1,
    imageAsset2,
    imageAsset3,
    imageAsset4,
    videoAsset,
    textAssets,
) {
    return [
        imageAsset1,
        imageAsset2,
        imageAsset3,
        imageAsset4,
        videoAsset,
        ...textAssets,
    ].filter(asset => isNotEmptyObject(asset));
}

function NewPropertiesAssetUpload({
    item,
    setItem,
    setUploadFile,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
    headerState,
    setHeaderState,
}) {
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [file, setFile] = useState(null);
    const { enqueueUpload } = useUploadContext();
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [textAssets, setTextAssets] = useState([]);
    const [selectedContainerAssets, setSelectedContainerAssets] = useState([]);
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCard, setActiveCard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [imageOpen, setImageOpen] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);
    const imageId = imageOpen ? 'simple-popover' : undefined;
    const videoId = videoOpen ? 'simple-popover' : undefined;
    const [imageAnchorEl, setImageAnchorEl] = useState(null);
    const [videoAnchorEl, setVideoAnchorEl] = useState(null);
    const latestRequestIdRef = useRef(0);

    const {
        isNoneChecked,
        isImageChecked,
        isVideoChecked,
        imageAsset1,
        imageAsset2,
        imageAsset3,
        imageAsset4,
        videoAsset,
        videoUrl,
        isExternal,
        isInitialized,
    } = headerState;

    function updateHeaderState(updates) {
        setHeaderState(prev => ({ ...prev, ...updates }));
    }

    function getVideoPreviewUrl() {
        if (isExternal) {
            return videoUrl || null;
        }
        return videoAsset?.url || null;
    }

    function handleImagePopoverClick(event) {
        setImageAnchorEl(event.currentTarget);
        setImageOpen(!imageOpen);
    }

    function handleVideoPopoverClick(event) {
        setVideoAnchorEl(event.currentTarget);
        setVideoOpen(!videoOpen);
    }

    function handlePopoverClose() {
        setImageAnchorEl(null);
        setVideoAnchorEl(null);
        setImageOpen(false);
        setVideoOpen(false);
    }

    useEffect(() => {
        if (!isVideoChecked) {
            updateHeaderState({ videoAsset: {}, videoUrl: '' });
        }
    }, [isVideoChecked]);

    useEffect(() => {
        if (!isImageChecked) {
            updateHeaderState({
                imageAsset1: {},
                imageAsset2: {},
                imageAsset3: {},
                imageAsset4: {},
            });
        }
    }, [isImageChecked]);

    useEffect(() => {
        handleUploadFromDevice(isUploadFromDeviceChecked);
    }, [isUploadFromDeviceChecked]);

    useEffect(() => {
        handleAddFromAssets(isAddFromAssetsChecked);
    }, [isAddFromAssetsChecked]);

    useEffect(() => {
        const imagesUrls = buildImageUrls(
            imageAsset1,
            imageAsset2,
            imageAsset3,
            imageAsset4,
        );

        const containerAssets = buildContainerAssets(
            imageAsset1,
            imageAsset2,
            imageAsset3,
            imageAsset4,
            videoAsset,
            textAssets,
        );

        setItem(prevState => {
            const { assetUploadPart } = prevState;
            return {
                ...prevState,
                assetUploadPart: {
                    ...assetUploadPart,
                    imageChecked: isImageChecked,
                    videoChecked: isVideoChecked,
                    video: videoUrl !== '' ? videoUrl : (videoAsset.url ?? ''),
                    images: imagesUrls,
                    videoIsExternal: isExternal,
                    textType: 'html',
                },
                containerAssets,
            };
        });
    }, [
        isImageChecked,
        isVideoChecked,
        videoAsset,
        isExternal,
        videoUrl,
        imageAsset1,
        imageAsset2,
        imageAsset3,
        imageAsset4,
        textAssets,
    ]);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsLoading(true);
            if (item === undefined) {
                updateHeaderState({ isInitialized: true });
                setIsLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            let tempVideoAsset = {};
            let tempImageAsset1 = {};
            let tempImageAsset2 = {};
            let tempImageAsset3 = {};
            let tempImageAsset4 = {};
            const tempTextAssets = [];

            const hasContainerAssets =
                'containerAssets' in item &&
                item.containerAssets !== null &&
                item.containerAssets.length > 0;

            if (hasContainerAssets) {
                item.containerAssets = item.containerAssets.filter(
                    assetItem => Object.keys(assetItem).length > 0,
                );

                const orderedContainerAssets = [...item.containerAssets].sort(
                    (a, b) => a.assetOrder - b.assetOrder,
                );

                for (const containerAsset of orderedContainerAssets) {
                    if (!containerAsset.assetId) {
                        continue;
                    }

                    const fetchedAsset = await fetchAssetByID(
                        containerAsset.assetId,
                        null,
                    );
                    containerAsset.url = fetchedAsset.url;

                    const assetUploadText = item.assetUploadPart?.text;
                    const isInText =
                        typeof assetUploadText === 'string' &&
                        assetUploadText.indexOf(containerAsset.url) !== -1;

                    if (isInText) {
                        const regex = new RegExp(
                            `(<img[^>]+src=["']${containerAsset.url}["'][^>]+style=["']([^"']+)["'][^>]*>)`,
                            'i',
                        );
                        const match = assetUploadText.match(regex);
                        let width = null;
                        let margin = null;

                        if (match && match[2]) {
                            const style = match[2];
                            const widthMatch = style.match(/width:\s*([^;]+)/);
                            if (widthMatch) {
                                width = widthMatch[1].trim();
                            }
                            const marginMatch =
                                style.match(/margin:\s*([^;]+)/);
                            if (marginMatch) {
                                margin = marginMatch[1].trim();
                            }
                        }

                        tempTextAssets.push({
                            id: containerAsset.id,
                            assetId: containerAsset.assetId,
                            url: containerAsset.url,
                            width,
                            margin,
                        });
                        continue;
                    }

                    const fileCategory = getFileCategory(containerAsset.url);

                    if (fileCategory === 'video') {
                        tempVideoAsset = {
                            id: containerAsset.id,
                            assetId: containerAsset.assetId,
                            url: containerAsset.url,
                        };
                        continue;
                    }

                    if (fileCategory === 'image') {
                        const assetData = {
                            id: containerAsset.id,
                            assetId: containerAsset.assetId,
                            url: containerAsset.url,
                        };

                        if (!isNotEmptyObject(tempImageAsset1)) {
                            tempImageAsset1 = assetData;
                        } else if (!isNotEmptyObject(tempImageAsset2)) {
                            tempImageAsset2 = assetData;
                        } else if (!isNotEmptyObject(tempImageAsset3)) {
                            tempImageAsset3 = assetData;
                        } else if (!isNotEmptyObject(tempImageAsset4)) {
                            tempImageAsset4 = assetData;
                        }
                    }
                }
            }

            const hasImages = isNotEmptyObject(tempImageAsset1);
            const hasVideoAsset = isNotEmptyObject(tempVideoAsset);
            const hasExternalVideo =
                'assetUploadPart' in item &&
                item.assetUploadPart !== null &&
                item.assetUploadPart?.video !== null &&
                item.assetUploadPart?.video !== undefined &&
                item.assetUploadPart?.video !== '';

            setTextAssets(tempTextAssets);

            if (hasVideoAsset) {
                updateHeaderState({
                    imageAsset1: tempImageAsset1,
                    imageAsset2: tempImageAsset2,
                    imageAsset3: tempImageAsset3,
                    imageAsset4: tempImageAsset4,
                    videoAsset: tempVideoAsset,
                    videoUrl: '',
                    isExternal: false,
                    isImageChecked: hasImages,
                    isVideoChecked: true,
                    isNoneChecked: false,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if (hasExternalVideo) {
                updateHeaderState({
                    imageAsset1: tempImageAsset1,
                    imageAsset2: tempImageAsset2,
                    imageAsset3: tempImageAsset3,
                    imageAsset4: tempImageAsset4,
                    videoAsset: {},
                    videoUrl: item.assetUploadPart.video,
                    isExternal: true,
                    isImageChecked: hasImages,
                    isVideoChecked: true,
                    isNoneChecked: false,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if (hasImages) {
                updateHeaderState({
                    imageAsset1: tempImageAsset1,
                    imageAsset2: tempImageAsset2,
                    imageAsset3: tempImageAsset3,
                    imageAsset4: tempImageAsset4,
                    videoAsset: {},
                    videoUrl: '',
                    isExternal: false,
                    isImageChecked: true,
                    isVideoChecked: false,
                    isNoneChecked: false,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            updateHeaderState({
                imageAsset1: {},
                imageAsset2: {},
                imageAsset3: {},
                imageAsset4: {},
                videoAsset: {},
                videoUrl: '',
                isExternal: false,
                isImageChecked: false,
                isVideoChecked: false,
                isNoneChecked: true,
                isInitialized: true,
            });
            setIsLoading(false);
        }

        fetchInitial();
    }, [isInitialized]);

    function handleFileChange(fileValue) {
        setFile(sanitizeFileName(fileValue));
    }

    function handleDragStart(event, position) {
        event.dataTransfer.setData('text/plain', position);
    }

    function handleDrop(event, newPosition) {
        const oldPosition = event.dataTransfer.getData('text/plain');
        swapImages(parseInt(oldPosition, 10), newPosition);
        event.preventDefault();
    }

    function swapImages(oldIndex, newIndex) {
        const newContainerAssets = [
            imageAsset1,
            imageAsset2,
            imageAsset3,
            imageAsset4,
        ];
        const temp = newContainerAssets[oldIndex];
        newContainerAssets[oldIndex] = newContainerAssets[newIndex];
        newContainerAssets[newIndex] = temp;

        updateHeaderState({
            imageAsset1: newContainerAssets[0],
            imageAsset2: newContainerAssets[1],
            imageAsset3: newContainerAssets[2],
            imageAsset4: newContainerAssets[3],
        });
    }

    function handleDragOver(event) {
        event.preventDefault();
    }

    function toggleThumbnailSelection(index) {
        const currentContainerAssets = [
            imageAsset1,
            imageAsset2,
            imageAsset3,
            imageAsset4,
        ];
        if (!currentContainerAssets[index]?.url) {
            return;
        }

        const newSelection = [...selectedContainerAssets];
        const position = newSelection.indexOf(index);

        if (position > -1) {
            newSelection.splice(position, 1);
        } else {
            newSelection.push(index);
        }
        setSelectedContainerAssets(newSelection);
    }

    function deleteSelectedThumbnails() {
        const updates = {};
        const assetKeys = [
            'imageAsset1',
            'imageAsset2',
            'imageAsset3',
            'imageAsset4',
        ];
        selectedContainerAssets.forEach(index => {
            updates[assetKeys[index]] = {};
        });
        updateHeaderState(updates);
        setSelectedContainerAssets([]);
    }

    async function uploadAsset() {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                    setIsSaving(false);
                    return;
                }
                urlResponse = encodeURI(response);
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch {
                    Toast.error(getText('msg_3e298875de25'));
                    setIsSaving(false);
                    return;
                }
            } catch {
                Toast.error(getText('msg_3e298875de25'));
                setIsSaving(false);
                return;
            }
        }

        if (!isValidUrl && !asset.url) {
            setIsSaving(false);
            return;
        }

        const newAsset = { ...asset };
        if (!newAsset.assetTypeId) {
            newAsset.assetTypeId = 4;
        }
        if (urlResponse) {
            newAsset.url = urlResponse;
        }
        newAsset.fileType = file.name.split('.').pop();
        newAsset.fileSize = formatFileSize(file.size);

        const fileCategory = getFileCategory(file.name);
        const metadataHandlers = {
            video: () => getVideoRuntime(file),
            audio: () => getAudioLength(file),
            image: () => getImageDimensions(file),
        };

        if (fileCategory in metadataHandlers) {
            newAsset.metaData = await metadataHandlers[fileCategory]();
        }

        const createResponse = await createAsset(newAsset, null);

        if (createResponse.error) {
            Toast.error(createResponse.message);
            setIsSaving(false);
            return;
        }

        if (createResponse.status === 200 && createResponse.data.url) {
            Toast.success(getText('msg_9309271e92fe'));
            handleAssetUploadSuccess(createResponse.data);
            setIsUploadFromDeviceChecked('');
            setAsset({});
            setFile(null);
            setIsSaving(false);
            return;
        }

        Toast.error(getText('msg_9f8fd8f97fd3'));
        setIsSaving(false);
    }

    function handleAssetUploadSuccess(responseData) {
        const assetData = {
            assetId: responseData.id,
            assetTypeId: responseData.assetTypeId,
            url: responseData.url,
        };

        if (isUploadFromDeviceChecked === 'image') {
            const imageAssets = [
                imageAsset1,
                imageAsset2,
                imageAsset3,
                imageAsset4,
            ];
            const assetKeys = [
                'imageAsset1',
                'imageAsset2',
                'imageAsset3',
                'imageAsset4',
            ];
            const emptyIndex = imageAssets.findIndex(
                assetItem => !assetItem.url,
            );
            if (emptyIndex !== -1) {
                updateHeaderState({ [assetKeys[emptyIndex]]: assetData });
            }
            return;
        }

        if (isUploadFromDeviceChecked === 'video') {
            updateHeaderState({ videoAsset: assetData, videoUrl: '' });
            return;
        }

        if (isUploadFromDeviceChecked === 'text') {
            setTextAssets(prevTextAssets => {
                const assetExists = prevTextAssets.some(
                    assetItem => assetItem.assetId === responseData.id,
                );
                if (assetExists) {
                    return prevTextAssets;
                }
                return [...prevTextAssets, assetData];
            });
        }
    }

    function validateForm() {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    }

    function updateAsset(assetObj, key, value) {
        return { ...assetObj, [key]: value };
    }

    const loadAssets = useCallback(
        async (page = currentPage, search = searchTerm) => {
            latestRequestIdRef.current += 1;
            const requestId = latestRequestIdRef.current;

            setIsLoading(true);
            setIsSaving(true);

            try {
                let queryParams = {
                    assetTypes: [1, 2],
                    page,
                    pageSize: 12,
                };

                if (
                    isAddFromAssetsChecked === 'image' ||
                    isAddFromAssetsChecked === 'text'
                ) {
                    queryParams = {
                        ...queryParams,
                        fileTypes: ['jpg', 'jpeg', 'png', 'svg'],
                    };
                }

                if (isAddFromAssetsChecked === 'video') {
                    queryParams = { ...queryParams, fileTypes: ['mp4', 'mov'] };
                }

                if (search && search.trim() !== '') {
                    queryParams = { ...queryParams, search: search.trim() };
                }

                const fetchedAssets = await fetchAssets(
                    queryParams,
                    null,
                    false,
                );

                if (requestId !== latestRequestIdRef.current) {
                    return;
                }

                if (!fetchedAssets) {
                    setAssets([]);
                    setTotalPages(1);
                    return;
                }

                setAssets(fetchedAssets.viewModels || []);
                setTotalPages(fetchedAssets.pageTotal || 1);
            } catch (error) {
                console.error('Failed to fetch assets', error);
                Toast.error(getText('msg_cade149a85b2'));
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setIsLoading(false);
                    setIsSaving(false);
                }
            }
        },
        [currentPage, searchTerm, isAddFromAssetsChecked],
    );

    const debouncedLoadRef = useRef(
        debounce(term => {
            setCurrentPage(1);
            loadAssets(1, term);
        }, 400),
    );

    useEffect(() => {
        const debounced = debouncedLoadRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    function handleSearch(event) {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedLoadRef.current(value);

        if (value === '') {
            debouncedLoadRef.current.cancel();
            setCurrentPage(1);
            loadAssets(1, '');
        }
    }

    function handleSearchKeyDown(event) {
        if (event.key !== 'Enter') {
            return;
        }
        debouncedLoadRef.current.cancel();
        setCurrentPage(1);
        loadAssets(1, searchTerm);
    }

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [isAddFromAssetsChecked, currentPage]);

    function handleClick(index) {
        setActiveCard(activeCard === index ? null : index);
    }

    function handleURLChange(event) {
        updateHeaderState({ videoUrl: event.target.value, videoAsset: {} });
    }

    useEffect(() => {
        // Only clear values when user manually changes isExternal after initialization
        // Skip during initial load to prevent overwriting loaded data
        if (!isInitialized) {
            return;
        }

        if (isExternal) {
            updateHeaderState({ videoAsset: {} });
            return;
        }
        updateHeaderState({ videoUrl: '' });
    }, [isExternal, isInitialized]);

    function addAsset() {
        if (activeCard === null) {
            Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
            return;
        }

        const assetAdding = assets[activeCard];
        handleAssetAddFromLibrary(assetAdding);
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function handleAssetFromLibrarySelected(assetAdding) {
        handleAssetAddFromLibrary(assetAdding);
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function getAcceptedFileTypes() {
        if (isAddFromAssetsChecked === 'video') {
            return ['mp4', 'mov'];
        }

        return ['jpg', 'jpeg', 'png', 'svg'];
    }

    function handleAssetAddFromLibrary(assetAdding) {
        const assetData = {
            assetId: assetAdding.id,
            url: assetAdding.url,
        };

        if (isAddFromAssetsChecked === 'image') {
            const imageAssets = [
                imageAsset1,
                imageAsset2,
                imageAsset3,
                imageAsset4,
            ];
            const assetKeys = [
                'imageAsset1',
                'imageAsset2',
                'imageAsset3',
                'imageAsset4',
            ];
            const emptyIndex = imageAssets.findIndex(
                assetItem => !assetItem.url,
            );
            if (emptyIndex !== -1) {
                updateHeaderState({ [assetKeys[emptyIndex]]: assetData });
            }
            return;
        }

        if (isAddFromAssetsChecked === 'video') {
            updateHeaderState({ videoAsset: assetData, videoUrl: '' });
            return;
        }

        if (isAddFromAssetsChecked === 'text') {
            setTextAssets(prevTextAssets => {
                const assetExists = prevTextAssets.some(
                    assetItem => assetItem.assetId === assetAdding.id,
                );
                if (assetExists) {
                    return prevTextAssets;
                }
                return [...prevTextAssets, assetData];
            });
        }
    }

    function handleNoneCheck() {
        updateHeaderState({
            isNoneChecked: true,
            isImageChecked: false,
            isVideoChecked: false,
            imageAsset1: {},
            imageAsset2: {},
            imageAsset3: {},
            imageAsset4: {},
            videoAsset: {},
            videoUrl: '',
            isExternal: false,
        });
    }

    function handleImageCheck() {
        updateHeaderState({
            isNoneChecked: false,
            isImageChecked: true,
            isVideoChecked: false,
        });
    }

    function handleVideoCheck() {
        updateHeaderState({
            isNoneChecked: false,
            isImageChecked: false,
            isVideoChecked: true,
        });
    }

    function shouldHideForUploadOrAssets() {
        return isUploadFromDeviceChecked || isAddFromAssetsChecked;
    }

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <>
            <div
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    display: shouldHideForUploadOrAssets() ? 'none' : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                    }}
                >
                    {getText('msg_20e338624cee')}
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: 'rgba(47, 43, 67, 0.6)',
                    }}
                >
                    {getText('msg_b278a8f203cf')}
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: 'rgba(47, 43, 67, 0.6)',
                    }}
                >
                    {getText('msg_af7265d902a8')}{' '}
                    <strong>
                        <em>{getText('msg_1758356db217')}</em>
                    </strong>{' '}
                    {getText('msg_39376ecd0b6d')}
                </div>
            </div>

            <div
                style={{
                    display:
                        areInputsDisabled || shouldHideForUploadOrAssets()
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    className="form-check"
                    style={{ float: 'left', marginRight: '30px' }}
                >
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isNoneChecked}
                        onChange={handleNoneCheck}
                    />
                    <label className="form-check-label">
                        {getText('msg_6eef6648406c')}
                    </label>
                </div>
                <div
                    className="form-check"
                    style={{ float: 'left', marginRight: '30px' }}
                >
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isImageChecked}
                        onChange={handleImageCheck}
                    />
                    <label className="form-check-label">
                        {getText('msg_50e19fda0d5b')}
                    </label>
                </div>
                <div className="form-check" style={{ float: 'left' }}>
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isVideoChecked}
                        onChange={handleVideoCheck}
                    />
                    <label className="form-check-label">
                        {getText('msg_bc17c1f0179a')}
                    </label>
                </div>
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled ||
                        !isImageChecked ||
                        shouldHideForUploadOrAssets()
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <p
                        style={{
                            color: 'gray',
                            fontWeight: '500',
                            margin: '16px 0',
                        }}
                    >
                        {getText('msg_516c6ca63154')}
                    </p>
                    <span
                        onClick={handleImagePopoverClick}
                        role="button"
                        tabIndex={0}
                        className="button-base ing-button-primary-left"
                    >
                        <i className="bi bi-plus-lg me-1" />
                        {getText('msg_61cc55aa0453')}
                    </span>
                    <Popover
                        id={imageId}
                        open={imageOpen}
                        anchorEl={imageAnchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 0,
                                paddingBottom: 0,
                                paddingTop: 0,
                                lineHeight: '40px',
                            }}
                        >
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('image');
                                    setIsUploadFromDeviceChecked('');
                                    setImageOpen(false);
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_0bc609667aa7')}
                            </div>
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('');
                                    setIsUploadFromDeviceChecked('image');
                                    setImageOpen(false);
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_c002fe6df29c')}
                            </div>
                        </Box>
                    </Popover>
                </div>
                <div
                    style={{
                        display: 'flex',
                        height: '100%',
                        width: '100%',
                        margin: '20px 0',
                        alignItems: 'center',
                    }}
                >
                    {[imageAsset1, imageAsset2, imageAsset3, imageAsset4].map(
                        (src, index) => (
                            <div
                                key={`image-slot-${index}`}
                                draggable={!!src.url}
                                onDragStart={e => handleDragStart(e, index)}
                                onDrop={e => handleDrop(e, index)}
                                onDragOver={handleDragOver}
                                onClick={() => toggleThumbnailSelection(index)}
                                role="button"
                                tabIndex={0}
                                style={{
                                    flex: 1,
                                    cursor: src.url ? 'pointer' : 'auto',
                                    margin: '5px',
                                    height: '212px',
                                    width: '208px',
                                    backgroundImage: src.url
                                        ? `url("${src.url}")`
                                        : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    outlineColor: src.url
                                        ? '#1366ff'
                                        : 'lightgrey',
                                    outlineStyle:
                                        src.url &&
                                        selectedContainerAssets.includes(index)
                                            ? 'solid'
                                            : 'dashed',
                                    outlineWidth: '1px',
                                }}
                            >
                                {!src.url && <ImageIcon />}
                                <i
                                    onClick={deleteSelectedThumbnails}
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                        margin: '10px 10px 0px 0px',
                                        fontSize: '1.2em',
                                        color: 'red',
                                        cursor: 'pointer',
                                        display:
                                            src.url &&
                                            selectedContainerAssets.includes(
                                                index,
                                            )
                                                ? 'block'
                                                : 'none',
                                        float: 'right',
                                    }}
                                    className="bi bi-trash3"
                                />
                            </div>
                        ),
                    )}
                </div>
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled ||
                        !isVideoChecked ||
                        shouldHideForUploadOrAssets()
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <p
                        style={{
                            color: 'gray',
                            fontWeight: '500',
                            margin: '16px 0',
                        }}
                    >
                        {getText('msg_86474f530357')}
                    </p>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                marginRight: '10px',
                                display:
                                    isExternal && videoUrl ? 'block' : 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() =>
                                updateHeaderState({
                                    videoUrl: '',
                                    videoAsset: {},
                                })
                            }
                        >
                            <i
                                style={{
                                    fontSize: '1.2em',
                                    color: 'red',
                                }}
                                className="bi bi-trash3"
                            />{' '}
                            {getText('msg_247fd63183c7')}
                        </div>
                        <span
                            onClick={handleVideoPopoverClick}
                            role="button"
                            tabIndex={0}
                            className="button-base ing-button-primary-left"
                        >
                            <i className="bi bi-plus-lg me-1" />
                            {getText('msg_61cc55aa0453')}
                        </span>
                    </div>

                    <Popover
                        id={videoId}
                        open={videoOpen}
                        anchorEl={videoAnchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 0,
                                paddingBottom: 0,
                                paddingTop: 0,
                                lineHeight: '40px',
                            }}
                        >
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('video');
                                    updateHeaderState({
                                        isExternal: false,
                                        videoUrl: '',
                                    });
                                    setIsUploadFromDeviceChecked('');
                                    setVideoOpen(false);
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_0bc609667aa7')}
                            </div>
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('');
                                    updateHeaderState({
                                        isExternal: false,
                                        videoUrl: '',
                                    });
                                    setIsUploadFromDeviceChecked('video');
                                    setVideoOpen(false);
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_c002fe6df29c')}
                            </div>
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('');
                                    updateHeaderState({
                                        isExternal: true,
                                        videoAsset: {},
                                    });
                                    setIsUploadFromDeviceChecked('');
                                    setVideoOpen(false);
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_0b9c93c200b9')}
                            </div>
                        </Box>
                    </Popover>
                </div>
                <div
                    style={{
                        width: '100%',
                        minWidth: '100%',
                        margin: 'auto',
                        paddingTop: '26px',
                    }}
                >
                    {getVideoPreviewUrl() ? (
                        <div
                            style={{
                                height: '130px',
                                width: '210px',
                                margin: '0px 1.5rem',
                            }}
                        >
                            <Video url={getVideoPreviewUrl()} caption="" />
                        </div>
                    ) : (
                        <VideoIcon />
                    )}
                    <div
                        style={{
                            marginTop: '46px',
                            display: isExternal ? 'block' : 'none',
                        }}
                    >
                        <div
                            style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: 'rgba(47, 43, 67, 1)',
                                marginBottom: '8px',
                            }}
                        >
                            {getText('msg_4580dbaee8e6')}
                        </div>
                        <input
                            onChange={handleURLChange}
                            placeholder={getText('msg_85eccc61fc42')}
                            value={videoUrl}
                            style={{
                                height: '48px',
                                borderRadius: '12px',
                                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            }}
                            className="form-control"
                            type="text"
                            aria-label={getText('msg_f0aa41e6ba0a')}
                            disabled={areInputsDisabled}
                        />
                    </div>
                </div>
            </div>

            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAsset(asset, 'name', value))
                        }
                        maxChars={256}
                        required
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                />
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(updateAsset(asset, 'assetTags', tags))
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAsset(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_5abd905257ee')}
                            checked={asset.assetTypeId !== 5}
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                        }}
                    >
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={uploadAsset}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {isAddFromAssetsChecked && (
                <AddFromAssetsModal
                    title="Add from Library"
                    onYes={handleAssetFromLibrarySelected}
                    onNo={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    onHide={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    yesButtonText="Add"
                    noButtonText="Cancel"
                    acceptedFileTypes={getAcceptedFileTypes()}
                    assetTypes={[1, 2]}
                    wasCreatedByMe={false}
                    displayModalBackdrop={false}
                />
            )}
        </>
    );
}

export default NewPropertiesAssetUpload;
